import { useEffect, useRef } from 'react';
import { DiffEditor, DiffEditorProps } from '@monaco-editor/react';
import type monaco from 'monaco-editor';

export type WbDiffEditorProps = DiffEditorProps;
export type MonacoDiffEditor = monaco.editor.IStandaloneDiffEditor;

/**
 * A wrapper component for the DiffEditor component from the @monaco-editor/react library.
 * @param {WbDiffEditorProps} props - The props for the WbDiffEditor component.
 * @returns {JSX.Element} - A React component that renders the DiffEditor component.
 */
export const WbDiffEditor = (props: WbDiffEditorProps): JSX.Element => {
  const {
    original,
    modified,
    theme,
    language,
    onMount: customOnMount,
    ...wbProps
  } = props;

  // Use a ref to store the editor instance for cleanup
  const editorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);

  // Handle cleanup when component unmounts
  useEffect(() => {
    // Return cleanup function
    return () => {
      // If we have an editor instance, manually clean up the models
      if (editorRef.current) {
        // Get the original and modified models
        const diffEditor = editorRef.current;
        try {
          // Set models to null before they get disposed
          diffEditor.setModel(null);
        } catch (e) {
          // console.warn('Error during editor cleanup:', e);
        }
      }
    };
  }, []);

  return (
    <DiffEditor
      original={original}
      modified={modified}
      theme={theme ?? 'default'}
      language={language ?? 'yaml'}
      onMount={(editorInstance, monaco) => {
        // Store references for cleanup
        editorRef.current = editorInstance;
        monacoRef.current = monaco;
        if (editorInstance) {
          editorInstance.updateOptions({ scrollBeyondLastLine: false });
          if (customOnMount) customOnMount(editorInstance, monaco);
        }
      }}
      {...wbProps}
    />
  );
};
